import { getNewTranscriptStatus, createNewTranscript, getNewTranscript } from './newTranscriptService';
import { getVideoId } from '@/utils/youtube';
import { useVideoStore } from '@/stores/videoStore';
import { ref } from 'vue';
import apiService from './apiService';
import pollingService from './pollingService';
import { notifyError } from '@/utils/uiStateManager';

// Kullanıcıdan gelen arama sorgusundan (YouTube URL'si) videoId'yi çıkarır, store'a kaydeder, loading başlatır ve videoId'yi döner.
export async function handleSearch(searchQuery: string): Promise<string | null> {
  const videoStore = useVideoStore();
  if (!searchQuery) return null;
  const extractedVideoId = getVideoId(searchQuery);
  if (!extractedVideoId) return null;
  videoStore.setVideoData({
    id: extractedVideoId,
    url: searchQuery,
    loading: true,
    error: null
  });
  videoStore.setVideoId(extractedVideoId);
  return extractedVideoId;
}

// Store'dan videoId ve seçilen dili alır, loading ve spinner state'lerini yönetir, transcript/özet işlemlerini başlatır.
export async function processVideoWithLanguage(language: string, options: { skipPolling?: boolean } = {}) {
  const videoStore = useVideoStore();
  const videoId = videoStore.videoId;
  if (!videoId) return;
  // Loading ve spinner state'lerini ayarla (örnek, ihtiyaca göre geliştirilebilir)
  videoStore.setLoadingState('summary', true);
  videoStore.setLoadingState('transcript', true);
  videoStore.setLoadingState('processing', true);
  // Transcript ve özet işlemlerini başlat
  await handleVideoProcess(videoId, language);
}

// Transcript ve özet işlemlerini başlatan ana fonksiyon
export async function handleVideoProcess(videoId: string, language: string) {
  try {
    // Polling mekanizmasını başlat
    setupRealTimeUpdates(videoId, language);
    
    // Önce transcript'in durumu sorgulanır
    let status = await getNewTranscriptStatus(videoId, language);
    if (status.status === 'not_found') {
      // Transcript yoksa oluştur
      await createNewTranscript(videoId, language);
      // Oluşturduktan sonra tekrar status sorgula
      status = await getNewTranscriptStatus(videoId, language);
    }
    
    // Transcript hazırsa içeriğini getir ve summary oluştur
    if (status.status === 'completed') {
      const transcript = await getNewTranscript(videoId, language);
      
      // Transcript tamamlandıysa UI'ı güncelle
      if (transcript && transcript.formatted_text) {
        handleTranscriptComplete(transcript);
        
        // Summary oluşturmayı başlat
        try {
          const summaryResponse = await createSummary(videoId, language, false, transcript.id);
          if (summaryResponse && summaryResponse.content) {
            handleSummaryComplete(summaryResponse);
          }
        } catch (summaryError) {
          console.error('Summary creation error:', summaryError);
          // Summary hatası transcript işlemini etkilemesin
        }
      }
      
      return transcript;
    } else {
      // Polling devam ediyor, status'u dön
      return status;
    }
  } catch (error) {
    // Hata durumunda spinner'ları kapat
    const videoStore = useVideoStore();
    videoStore.setLoadingState('transcript', false);
    videoStore.setLoadingState('summary', false);
    videoStore.setLoadingState('processing', false);
    
    throw new Error('Transcript işlemi sırasında hata: ' + (error instanceof Error ? error.message : error));
  }
}

// Transcript oluşturma/getirme fonksiyonu
export async function createTranscript(videoId: string, language: string) {
  try {
    // Transcript oluştur veya getir
    const result = await createNewTranscript(videoId, language);
    return result;
  } catch (error) {
    throw new Error('Transcript oluşturulurken hata: ' + (error instanceof Error ? error.message : error));
  }
}

// Varsayılan videoId ile ana sayfada video ve transcript/özet yükler.
// Eğer skipPolling veya isFromRecent opsiyonu verilirse, sadece mevcut verileri yükler.
export async function loadInitialVideo(videoId: string, options: { skipPolling?: boolean, isFromRecent?: boolean } = {}) {
  const videoStore = useVideoStore();
  try {
    videoStore.setLoadingState('video', true);
    videoStore.setVideoId(videoId);
    videoStore.setVideoData({
      id: videoId,
      url: `https://www.youtube.com/watch?v=${videoId}`,
      loading: true,
      error: null
    });
    if (options.skipPolling || options.isFromRecent) {
      // Sadece mevcut transcript'i getir
      const transcript = await getNewTranscript(videoId, 'tr');
      if (transcript && transcript.formatted_text) {
        videoStore.setVideoData({
          formatted_text: transcript.formatted_text,
          transcript: transcript.formatted_text,
          transcriptPreview: transcript.formatted_text.substring(0, 400)
        });
      }
      // (Özet için benzer bir yapı eklenebilir)
      videoStore.setLoadingState('transcript', false);
      videoStore.setLoadingState('summary', false);
      videoStore.setLoadingState('processing', false);
    } else {
      // Normal işleme - transcript ve özet işlemlerini başlat
      await processVideoWithLanguage('tr');
    }
  } catch (err) {
    videoStore.setVideoData({
      loading: false,
      error: err instanceof Error ? err.message : 'Failed to load video'
    });
  } finally {
    videoStore.setLoadingState('video', false);
  }
}

// Belirli bir dil ve/veya limit ile mevcut özetleri backend'den çeker (mevcut summary endpointi ile).
export async function loadAvailableSummaries(params: { language?: string, limit?: number } = {}): Promise<any[]> {
  // Eski apiService fonksiyonunu kullan
  const response = await apiService.getAvailableSummaries();
  let summaries: any[] = [];
  if (Array.isArray(response)) {
    summaries = response;
  } else if (response && typeof response === 'object' && Array.isArray((response as any).data)) {
    summaries = (response as any).data;
  }
  // Dil filtresi uygula
  if (params.language) {
    summaries = summaries.filter((summary: any) => summary.language === params.language);
  }
  // Limit uygula
  if (params.limit && params.limit > 0) {
    summaries = summaries.slice(0, params.limit);
  }
  return summaries;
}

// Bir video için özet (summary) oluşturur. Yeni transcript yapısını kullanır.
export async function createSummary(
  videoId: string,
  language: string,
  isPublic = false,
  transcriptId?: string
): Promise<any> {
  try {
    // Önce transcript'i kontrol et
    if (!transcriptId) {
      // Eğer transcriptId verilmemişse, transcript'i bul
      const transcriptResponse = await getNewTranscript(videoId, language);
      if (transcriptResponse && transcriptResponse.id) {
        transcriptId = transcriptResponse.id;
      } else {
        throw new Error('Transcript bulunamadı. Özet oluşturulamıyor.');
      }
    }

    // API parametrelerini hazırla
    const params: any = {
      videoId,
      language,
      is_public: isPublic,
      transcript_id: transcriptId // Her zaman transcript_id gönder
    };

    // Özet oluştur
    const response = await apiService.createSummaryFromVideo(params);
    return response;
  } catch (error) {
    console.error('Summary creation error:', error);
    throw new Error('Summary oluşturulurken hata: ' + (error instanceof Error ? error.message : error));
  }
}

// Bir kanalın tüm videoları için transcript oluşturma işlemini başlatır (yeni transcript servis fonksiyonları ile)
export async function processVideosForChannel(channelId: string, language: string): Promise<{
  success: boolean;
  processedCount: number;
  failedCount: number;
  message: string;
}> {
  try {
    // 1. Kanal videolarını çek
    const response = await apiService.getChannelVideos(channelId);
    const videos = response && response.data && Array.isArray(response.data) ? response.data : [];
    if (!videos.length) {
      return {
        success: false,
        processedCount: 0,
        failedCount: 0,
        message: `No videos found for channel ${channelId}`
      };
    }
    let successCount = 0;
    let failedCount = 0;
    // 2. Her video için transcript oluştur
    for (const video of videos) {
      try {
        const videoId = video.video_id;
        // Önce transcript var mı kontrol et
        let status = await getNewTranscriptStatus(videoId, language);
        if (status.status === 'not_found') {
          // Transcript yoksa oluştur
          await createNewTranscript(videoId, language);
          // Oluşturduktan sonra tekrar kontrol et
          status = await getNewTranscriptStatus(videoId, language);
        }
        if (status.status === 'completed') {
          successCount++;
        } else {
          failedCount++;
        }
        // API'yi yormamak için 500ms bekle
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        failedCount++;
      }
    }
    return {
      success: true,
      processedCount: successCount,
      failedCount,
      message: `Processed ${successCount} videos for channel ${channelId}, failed: ${failedCount}`
    };
  } catch (error) {
    return {
      success: false,
      processedCount: 0,
      failedCount: 0,
      message: `Error processing channel: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/*
 * debugVideoState Fonksiyonu (Yorum Olarak)
 *
 * Amaç:
 *   Bir videonun transcript ve summary işleme durumunu hem UI tarafında hem de backend'de kontrol eder.
 *   Eğer bir tutarsızlık veya "takılı kalmış" bir durum varsa, otomatik olarak düzeltmeye (ör. spinner'ları kapatmaya) çalışır.
 *
 * Adım Adım Akış:
 * 1. Mevcut UI durumunu loglar (hangi video işleniyor, transcript/summary spinner'ları açık mı, store'daki loading state'ler ne durumda).
 * 2. (Opsiyonel) Backend durumunu sorgular (transcript ve summary status API ile çekilir, UI'daki loading durumlarıyla karşılaştırılır).
 * 3. Tutarsızlıkları tespit eder (ör: backend'de tamamlanmış ama UI'da spinner dönüyor, backend'de hata var ama UI loading'de, vs).
 * 4. Otomatik düzeltme yapar (gerekirse transcript/summary'yi tekrar backend'den çeker, store'a yazar, loading ve spinner state'lerini kapatır, processing flag'ini sıfırlar, force close fonksiyonunu çağırır).
 * 5. Son durumu tekrar loglar.
 *
 * Kullanım Senaryosu:
 * - UI'da transcript veya summary spinner'ı "takılı" kalırsa
 * - Backend'de iş tamamlanmış ama UI güncellenmemişse
 * - Admin/debug panelinde bir videonun işleme durumunu hızlıca düzeltmek istersen
 * - Otomatik kurtarma (recovery) mekanizması olarak
 *
 * Kısa Özet:
 * debugVideoState, bir videonun transcript/summary işleme durumunu hem UI'da hem backend'de kontrol eder, tutarsızlık varsa otomatik olarak düzeltir ve spinner'ları kapatır.
 */

// Bir videonun en güncel transcript bilgisini backend'den alır ve döndürür (yeni transcript servisi ile)
export async function fetchLatestTranscript(videoId: string, language: string = 'tr') {
  try {
    // Yeni transcript servis fonksiyonunu kullan
    const response = await getNewTranscript(videoId, language);
    // İstersen burada store'a da yazabilirsin
    return response;
  } catch (error) {
    // Hata olursa konsola logla
    console.error('fetchLatestTranscript error:', error);
    return null;
  }
}

// Bir videonun en güncel summary bilgisini backend'den alır ve döndürür (eski summary endpointi ile)
export async function fetchLatestSummary(videoId: string, language: string = 'tr') {
  try {
    // Eski summary endpointini kullan
    const response = await apiService.getSummary(videoId, language);
    // İstersen burada store'a da yazabilirsin
    return response;
  } catch (error) {
    // Hata olursa konsola logla
    console.error('fetchLatestSummary error:', error);
    return null;
  }
}

// Transcript veya summary metninin ilk X karakterini döndürür (varsayılan 250). Uzunsa sonuna '...' ekler.
export function getTextPreview(transcript: { formatted_text?: string } | string, maxLength: number = 250): string {
  let text = '';
  if (typeof transcript === 'string') {
    text = transcript;
  } else if (transcript && typeof transcript === 'object' && typeof transcript.formatted_text === 'string') {
    text = transcript.formatted_text;
  }
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/*
// İşlem adımlarını günceller (ör: Fetching, Transcribing, Summarizing, Saving)
// Eğer videoStore'da setProcessingStep fonksiyonu varsa aktif edebilirsin.
export function updateProcessingStatus(step: string) {
  const videoStore = useVideoStore();
  // videoStore.setProcessingStep(step); // Eğer fonksiyon eklenirse açabilirsin
}
*/

// Transcript işlemi tamamlandığında store'u günceller
export function handleTranscriptComplete(transcript: { formatted_text?: string }) {
  const videoStore = useVideoStore();
  if (transcript && transcript.formatted_text) {
    videoStore.setVideoData({
      formatted_text: transcript.formatted_text,
      transcript: transcript.formatted_text,
      transcriptPreview: getTextPreview(transcript, 400)
    });
    videoStore.setLoadingState('transcript', false);
  }
}

// Summary işlemi tamamlandığında store'u günceller
export function handleSummaryComplete(summary: { content?: string }) {
  const videoStore = useVideoStore();
  if (summary && summary.content) {
    videoStore.setVideoData({
      summary: summary.content,
      summaryPreview: getTextPreview(summary.content, 250)
    });
    videoStore.setLoadingState('summary', false);
  }
}

// Polling/real-time güncelleme başlatır
export function setupRealTimeUpdates(videoId: string, language: string) {
  const videoStore = useVideoStore();
  
  // Önceki polling'i temizle
  pollingService.stopAllPolling(videoId);
  
  // Yeni polling başlat
  pollingService.startPolling(videoId, language, {
    onTranscriptComplete: (transcript: any) => {
      console.log('✅ Transcript completed:', transcript);
      if (transcript && transcript.formatted_text) {
        handleTranscriptComplete(transcript);
        
        // Transcript tamamlandığında summary oluşturmayı başlat
        createSummary(videoId, language, false, transcript.id)
          .then(summaryResponse => {
            if (summaryResponse && summaryResponse.content) {
              handleSummaryComplete(summaryResponse);
            }
          })
          .catch(error => {
            console.error('Error creating summary:', error);
            videoStore.setLoadingState('summary', false);
          });
      }
    },
    onSummaryComplete: (summary: any) => {
      console.log('✅ Summary completed:', summary);
      if (summary && summary.content) {
        handleSummaryComplete(summary);
      }
      // Summary tamamlandığında tüm polling'i durdur
      pollingService.stopAllPolling(videoId);
    },
    onError: (error: any) => {
      console.error('❌ Polling error:', error);
      notifyError('İşlem sırasında hata: ' + (error?.message || error));
      // Hata durumunda spinner'ları kapat
      videoStore.setLoadingState('transcript', false);
      videoStore.setLoadingState('summary', false);
      videoStore.setLoadingState('processing', false);
      // Polling'i durdur
      pollingService.stopAllPolling(videoId);
    }
  });
  
  // Güvenlik kontrolü: 5 dakika sonra hala polling devam ediyorsa durdur
  setTimeout(() => {
    if (pollingService.isPollingActiveForVideo(videoId)) {
      console.warn('⚠️ Polling timeout after 5 minutes');
      pollingService.stopAllPolling(videoId);
      // Spinner'ları kapat
      videoStore.setLoadingState('transcript', false);
      videoStore.setLoadingState('summary', false);
      videoStore.setLoadingState('processing', false);
    }
  }, 5 * 60 * 1000);
} 